import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { UpdateUserProfile } from "../../../api/user/User";
import { ProfileFormType } from "./Profile";
import { toast } from "react-toastify";

type Inputs = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  houseNumber: string;
  street: string;
  subDistrict: string;
  district: string;
  province: string;
  zipCode: string;
};

export interface ProfileFormProps {
  user?: ProfileFormType;
  isSubmit: (value: boolean) => void;
}

function ProfileForm({ user, isSubmit }: ProfileFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();
  const [isChange, setIsChange] = useState(false);
  const [defaultUser, setDefaultUser] = useState<ProfileFormType>();
  const handleCheckIsChange = (value: boolean) => {
    setIsChange(value);
  };

  const handleSetFormState = (user: ProfileFormType) => {
    if (user) {
      setValue("firstName", user.firstName ? user.firstName : "");
      setValue("lastName", user.lastName ? user.lastName : "");
      setValue("email", user.email ? user.email : "");
      setValue(
        "phone",
        user?.extension?.hotspot?.phone ? user?.extension?.hotspot?.phone : ""
      );
      setValue(
        "houseNumber",
        user?.extension?.hotspot?.houseNumber
          ? user.extension?.hotspot?.houseNumber
          : ""
      );
      setValue(
        "street",
        user?.extension?.hotspot?.street ? user.extension?.hotspot?.street : ""
      );
      setValue(
        "subDistrict",
        user?.extension?.hotspot?.subDistrict
          ? user.extension.hotspot?.subDistrict
          : ""
      );
      setValue(
        "district",
        user?.extension?.hotspot?.district
          ? user?.extension?.hotspot?.district
          : ""
      );
      setValue(
        "province",
        user?.extension?.hotspot?.province
          ? user.extension.hotspot?.province
          : ""
      );
      setValue(
        "zipCode",
        user?.extension?.hotspot?.zipCode
          ? user.extension?.hotspot?.zipCode
          : ""
      );
    }
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    let payload: any = {
      id: user?.id,
      accountType: user?.accountType,
      username: user?.username,
      email: data.email ?? "",
      firstName: data.firstName ?? "",
      lastName: data.lastName ?? "",
      role: user?.role,
      verified: user?.verified,
      avatar: user?.avatar ?? "",
      enabled: user?.enabled,
      lastLogin: user?.lastLogin,
      extension: {
        hotspot: {
          organization: user?.extension?.hotspot?.organization,
          phone: data.phone ?? "",
          houseNumber: data.houseNumber.trim() ?? "",
          street: data.street.trim() ?? "",
          subDistrict: data.subDistrict.trim() ?? "",
          district: data.district.trim() ?? "",
          province: data.province.trim() ?? "",
          zipCode: data.zipCode.trim() ?? "",
        },
      },
    };
    try {
      await UpdateUserProfile(payload);
      toast.success("บันทึกข้อมูลสำเร็จ", {
        position: "bottom-left",
        autoClose: 3000,
      });
      isSubmit(true);
      setIsChange(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDefaultData = () => {
    if (isChange) {
      if (defaultUser) {
        handleSetFormState(defaultUser);
        setIsChange(false);
      }
    }
  };

  useEffect(() => {
    if (user) {
      handleSetFormState(user);
      setDefaultUser(user);
    }
  }, [user]);
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="inline-flex flex-col gap-6 w-full"
    >
      <div className="flex flex-col gap-4">
        <div className="flex w-fit rounded-xl bg-primary-main bg-opacity-20">
          <div className="bg-primary-main p-1 rounded-l-xl">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="4"
                cy="4"
                r="4"
                transform="matrix(-1 0 0 1 16.0039 3)"
                stroke="white"
                strokeWidth="1.5"
              />
              <path
                d="M5 16.9347C5 16.0743 5.54085 15.3068 6.35109 15.0175V15.0175C10.004 13.7128 13.996 13.7128 17.6489 15.0175V15.0175C18.4591 15.3068 19 16.0743 19 16.9347V18.2502C19 19.4376 17.9483 20.3498 16.7728 20.1818L15.8184 20.0455C13.2857 19.6837 10.7144 19.6837 8.18162 20.0455L7.22721 20.1818C6.05171 20.3498 5 19.4376 5 18.2502V16.9347Z"
                stroke="white"
                strokeWidth="1.5"
              />
            </svg>
          </div>
          <p className="text-base font-normal text-primary-main self-center rounded-r-xl py-1 pr-4 pl-2">
            ข้อมูลส่วนบุคคล
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="firstName">
              ชื่อ
            </label>
            <input
              id="firstName"
              onInput={() => handleCheckIsChange(true)}
              placeholder="ชื่อ"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("firstName", { required: "กรุณากรอกชื่อ" })}
              onKeyPress={(event) => {
                if (!/^\S.*[a-zA-Z\s]*$/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
            {errors?.firstName && (
              <span className="text-xs text-error-main font-light leading-[18px]">
                {errors?.firstName?.message}
              </span>
            )}
          </div>
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="lastName">
              นามสกุล
            </label>
            <input
              id="lastName"
              onInput={() => handleCheckIsChange(true)}
              placeholder="นามสกุล"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("lastName", { required: "กรุณากรอกนามสกุล" })}
              onKeyPress={(event) => {
                if (!/^\S.*[a-zA-Z\s]*$/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
            {errors?.lastName && (
              <span className="text-xs text-error-main font-light leading-[18px]">
                {errors?.lastName?.message}
              </span>
            )}
          </div>
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="phone">
              หมายเลขโทรศัพท์
            </label>
            <input
              id="phone"
              onInput={() => handleCheckIsChange(true)}
              placeholder="หมายเลขโทรศัพท์"
              className="border-[1px] border-gray-2 h-10 bg-primary-contrast rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("phone")}
              onKeyPress={(event) => {
                if (!/^[0-9]+$/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
          </div>
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="email">
              อีเมล
            </label>
            <input
              id="email"
              onInput={() => handleCheckIsChange(true)}
              placeholder="อีเมล"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("email", {
                required: "กรุณากรอกอีเมล",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "กรุณากรอกอีเมลให้ถูกต้อง",
                },
              })}
              onKeyPress={(event) => {
                if (!/^\S.*[a-zA-Z\s]*$/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
            {errors?.email && (
              <span className="text-xs text-error-main font-light leading-[18px]">
                {errors?.email?.message}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex w-fit rounded-xl bg-primary-main bg-opacity-20">
          <div className="bg-primary-main p-2 min-w-[32px] rounded-l-xl ">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_1198_71936)">
                <path
                  d="M6.82448 15.8407H11.1756V10.0667C11.1756 9.70191 10.9493 9.46395 10.6025 9.46395H7.40512C7.05071 9.46395 6.82448 9.70191 6.82448 10.0667V15.8407ZM3.876 16.6577H14.0788C15.1496 16.6577 15.7755 16.0152 15.7755 14.9049V6.14078L14.5614 5.26834V14.5797C14.5614 15.0951 14.2975 15.3807 13.8224 15.3807H4.13239C3.64978 15.3807 3.38585 15.0951 3.38585 14.5797V5.27627L2.17177 6.14078V14.9049C2.17177 16.0152 2.79766 16.6577 3.876 16.6577ZM0 7.83013C0 8.15531 0.241308 8.46467 0.648514 8.46467C0.859657 8.46467 1.0331 8.34568 1.19146 8.21087L8.73989 1.54858C8.90576 1.38996 9.10938 1.38996 9.27525 1.54858L16.8237 8.21087C16.9745 8.34568 17.1479 8.46467 17.3591 8.46467C17.7135 8.46467 18 8.23466 18 7.85393C18 7.61599 17.9246 7.45737 17.7663 7.3146L9.91624 0.374752C9.35818 -0.124917 8.66445 -0.124917 8.0989 0.374752L0.241308 7.3146C0.0754086 7.45737 0 7.64771 0 7.83013ZM13.8827 4.25314L15.7755 5.93456V2.27032C15.7755 1.92135 15.5643 1.69928 15.2326 1.69928H14.4256C14.1014 1.69928 13.8827 1.92135 13.8827 2.27032V4.25314Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_1198_71936">
                  <rect width="18" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
          <p className="text-base font-normal text-primary-main self-center rounded-r-xl py-1 pr-4 pl-2">
            ข้อมูลที่อยู่
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="houseNumber">
              บ้านเลขที่/หมู่บ้าน
            </label>
            <input
              id="houseNumber"
              onInput={() => handleCheckIsChange(true)}
              placeholder="บ้านเลขที่/หมู่บ้าน"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("houseNumber")}
            />
          </div>
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="street">
              ถนน
            </label>
            <input
              id="street"
              onInput={() => handleCheckIsChange(true)}
              placeholder="ถนน"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("street")}
            />
          </div>
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="subDistrict">
              ตำบล
            </label>
            <input
              id="subDistrict"
              onInput={() => handleCheckIsChange(true)}
              placeholder="ตำบล"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("subDistrict")}
            />
          </div>
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="district">
              อำเภอ/เขต
            </label>
            <input
              id="district"
              onInput={() => handleCheckIsChange(true)}
              placeholder="อำเภอ/เขต"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("district")}
            />
          </div>
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="province">
              จังหวัด
            </label>
            <input
              id="province"
              onInput={() => handleCheckIsChange(true)}
              placeholder="จังหวัด"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("province")}
            />
          </div>
          <div className="col-span-2 md:col-span-1 flex flex-col w-full">
            <label className="text-text-primary text-sm" htmlFor="zipCode">
              รหัสไปรษณีย์
            </label>
            <input
              id="zipCode"
              onInput={() => handleCheckIsChange(true)}
              placeholder="รหัสไปรษณีย์"
              className="border-[1px] border-gray-2 bg-primary-contrast h-10 rounded-2xl text-text-primary text-sm py-4 pl-4 pr-6 focus:!outline-none"
              {...register("zipCode")}
            />
          </div>
        </div>
      </div>
      <div className="flex gap-2 self-start justify-between md:justify-end w-full md:self-end">
        <button
          type="button"
          onClick={() => handleDefaultData()}
          className="px-10 w-full md:w-auto py-2 h-10 border-[1px] border-gray-2 text-text-primary text-base font-normal rounded-xl"
        >
          ยกเลิก
        </button>
        <button
          disabled={!isChange}
          className="px-10 py-2 w-full md:w-auto h-10 bg-primary-main disabled:bg-gray-1 disabled:text-text-disabled hover:bg-opacity-80 text-base text-primary-contrast  rounded-xl"
        >
          บันทึก
        </button>
      </div>
    </form>
  );
}

export default ProfileForm;
